import * as Yup from "yup";

const resolveMessage = (message, t) =>
  typeof t === "function" ? t(message) : message;

const mapYupErrors = (error) => {
  if (!error) return {};
  if (Array.isArray(error.inner) && error.inner.length > 0) {
    return error.inner.reduce((acc, item) => {
      const path = item?.path;
      if (!path || acc[path]) return acc;
      acc[path] = item.message;
      return acc;
    }, {});
  }
  if (error.path) {
    return { [error.path]: error.message };
  }
  return {};
};

export const getCompanyStepFieldKeys = (step = 1) => {
  switch (Number(step)) {
    case 1:
      return ["companyName", "country"];
    case 2:
      return ["industry"];
    case 3:
      return ["employeesRange", "revenueRange"];
    case 4:
      return ["serviceCountries", "objectives"];
    case 5:
      return [
        "ghgBaselineMode",
        "ghgBaselineReportingYear",
        "ghgBaselineScope1",
        "ghgBaselineScope2",
        "ghgBaselineScope3",
        "ghgBaselineEvidence",
      ];
    case 6:
      return [
        "targetFrequency",
        "dashboardVariant",
        "allowAssetList",
        "enableMonthlyYearlySwitch",
        "enableViewDetailsSwitch",
        "useJJOrganizationSelector",
        "showOnlyMetricsWithTargetAndData",
      ];
    default:
      return [];
  }
};

export const buildCompanyStepSchema = (step, t) => {
  const message = (text) => resolveMessage(text, t);
  switch (Number(step)) {
    case 1:
      return Yup.object({
        companyName: Yup.string()
          .trim()
          .required(message("Please enter your company name.")),
        country: Yup.string()
          .trim()
          .required(message("Please select a country.")),
        targetFrequency: Yup.string().oneOf(["yearly", "monthly"]),
        dashboardVariant: Yup.string().oneOf(["standard", "benchmark"]),
        allowAssetList: Yup.boolean(),
        enableMonthlyYearlySwitch: Yup.boolean(),
        enableViewDetailsSwitch: Yup.boolean(),
        useJJOrganizationSelector: Yup.boolean(),
        showOnlyMetricsWithTargetAndData: Yup.boolean(),
      });
    case 2:
      return Yup.object({
        industry: Yup.string()
          .trim()
          .required(message("Please select an industry.")),
      });
    case 3:
      return Yup.object({
        employeesRange: Yup.string()
          .trim()
          .required(message("Please select an employees range.")),
        revenueRange: Yup.string()
          .trim()
          .required(message("Please select a revenue range.")),
      });
    case 4:
      return Yup.object({
        serviceCountries: Yup.array().of(Yup.string()),
        objectives: Yup.array().of(Yup.string()),
      });
    case 5:
      return Yup.object({
        // "" is the deliberate "not configured yet" state — see
        // GHG_BASELINE_FORM_DEFAULTS in companyForm.mapper.js.
        ghgBaselineMode: Yup.string().oneOf(["", "reportingYear", "custom"]),
        ghgBaselineReportingYear: Yup.string(),
        ghgBaselineScope1: Yup.string(),
        ghgBaselineScope2: Yup.string(),
        ghgBaselineScope3: Yup.string(),
        ghgBaselineEvidence: Yup.mixed().nullable(),
      });
    case 6:
      return Yup.object({
        targetFrequency: Yup.string().oneOf(["yearly", "monthly"]),
        dashboardVariant: Yup.string().oneOf(["standard", "benchmark"]),
        allowAssetList: Yup.boolean(),
        enableMonthlyYearlySwitch: Yup.boolean(),
        enableViewDetailsSwitch: Yup.boolean(),
        useJJOrganizationSelector: Yup.boolean(),
        showOnlyMetricsWithTargetAndData: Yup.boolean(),
      });
    default:
      return null;
  }
};

export const buildCompanySubmitSchema = (t) => {
  const message = (text) => resolveMessage(text, t);
  return Yup.object({
    companyName: Yup.string()
      .trim()
      .required(message("Please enter your company name.")),
    country: Yup.string().trim().required(message("Please select a country.")),
    industry: Yup.string()
      .trim()
      .required(message("Please select an industry.")),
    employeesRange: Yup.string()
      .trim()
      .required(message("Please select an employees range.")),
    revenueRange: Yup.string().trim(),
    serviceCountries: Yup.array().of(Yup.string()),
    objectives: Yup.array().of(Yup.string()),
    targetFrequency: Yup.string().oneOf(["yearly", "monthly"]),
    dashboardVariant: Yup.string().oneOf(["standard", "benchmark"]),
    allowAssetList: Yup.boolean(),
    enableMonthlyYearlySwitch: Yup.boolean(),
    enableViewDetailsSwitch: Yup.boolean(),
    useJJOrganizationSelector: Yup.boolean(),
    showOnlyMetricsWithTargetAndData: Yup.boolean(),
    ghgBaselineMode: Yup.string().oneOf(["", "reportingYear", "custom"]),
    ghgBaselineReportingYear: Yup.string(),
    ghgBaselineScope1: Yup.string(),
    ghgBaselineScope2: Yup.string(),
    ghgBaselineScope3: Yup.string(),
    ghgBaselineEvidence: Yup.mixed().nullable(),
  });
};

export const validateCompanyFormStep = (step = 1, form = {}, t) => {
  const schema = buildCompanyStepSchema(step, t);
  if (!schema) return {};
  try {
    schema.validateSync(form, { abortEarly: false });
    return {};
  } catch (error) {
    return mapYupErrors(error);
  }
};

export const validateCompanySubmitForm = (form = {}, t) => {
  const schema = buildCompanySubmitSchema(t);
  try {
    schema.validateSync(form, { abortEarly: false });
    return {};
  } catch (error) {
    return mapYupErrors(error);
  }
};
